import { useState, useId } from 'react';
import * as d3 from 'd3';

// ─── DonutChart ───────────────────────────────────────────────────────────────
//
// Interaktives Ring-/Kreisdiagramm. Hovern (Desktop) bzw. Tippen (Touch) auf ein
// Segment oder einen Legendeneintrag hebt es hervor, dimmt die übrigen und zeigt
// in der Mitte Label + Anteil. Ohne Auswahl steht die Gesamtsumme im Zentrum.

export interface DonutDatum {
  label: string;
  count: number;
}

const COLORS = [
  '#3b82f6', '#ec4899', '#8b5cf6', '#10b981', '#f59e0b',
  '#06b6d4', '#f97316', '#6366f1', '#14b8a6', '#eab308',
];
const OTHER_COLOR = '#94a3b8'; // „Andere" bewusst neutral-grau

function truncate(text: string, max: number): string {
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function colorFor(d: DonutDatum, i: number): string {
  return d.label === 'Andere' ? OTHER_COLOR : COLORS[i % COLORS.length];
}

interface DonutChartProps {
  data: DonutDatum[];
  /** Kantenlänge des SVG in px. */
  size?: number;
  /** Untertitel im Zentrum, wenn nichts ausgewählt ist (z. B. „Personen"). */
  centerLabel?: string;
}

export function DonutChart({ data, size = 176, centerLabel = 'gesamt' }: DonutChartProps) {
  const [active, setActive] = useState<number | null>(null);
  const baseId = useId();

  const total = data.reduce((sum, d) => sum + d.count, 0);
  if (data.length === 0 || total === 0) return null;

  const r = size / 2;
  const innerR = r * 0.6;

  const pie = d3.pie<DonutDatum>().value((d) => d.count).sort(null);
  const arcs = pie(data);
  const arc = d3.arc<d3.PieArcDatum<DonutDatum>>().innerRadius(innerR).outerRadius(r - 6);
  const arcActive = d3.arc<d3.PieArcDatum<DonutDatum>>().innerRadius(innerR).outerRadius(r - 1);

  const pct = (count: number) => Math.round((count / total) * 100);

  const activeDatum = active != null ? data[active] : null;
  const centerPrimary = activeDatum ? `${pct(activeDatum.count)}%` : String(total);
  const centerSecondary = activeDatum ? truncate(activeDatum.label, 16) : centerLabel;

  const select = (i: number) => setActive((cur) => (cur === i ? null : i));

  return (
    <div className="flex flex-col items-center gap-4 sm:flex-row">
      <svg
        width={size}
        height={size}
        viewBox={`${-r} ${-r} ${size} ${size}`}
        className="shrink-0"
        role="img"
        aria-label={`Kreisdiagramm: ${data.map((d) => `${d.label} ${pct(d.count)}%`).join(', ')}`}
      >
        {arcs.map((a, i) => (
          <path
            key={`${baseId}-${data[i].label}`}
            d={(active === i ? arcActive : arc)(a) ?? undefined}
            fill={colorFor(data[i], i)}
            opacity={active == null || active === i ? 1 : 0.35}
            className="cursor-pointer transition-opacity duration-150"
            onMouseEnter={() => setActive(i)}
            onMouseLeave={() => setActive(null)}
            onClick={() => select(i)}
          >
            <title>{`${data[i].label}: ${data[i].count} (${pct(data[i].count)}%)`}</title>
          </path>
        ))}

        {/* Zentrum */}
        <text textAnchor="middle" dominantBaseline="central" y={-7} className="fill-slate-800" style={{ fontSize: 22, fontWeight: 700 }}>
          {centerPrimary}
        </text>
        <text textAnchor="middle" dominantBaseline="central" y={15} className="fill-slate-500" style={{ fontSize: 11 }}>
          {centerSecondary}
        </text>
      </svg>

      {/* Legende (mit dem Diagramm synchronisiert) */}
      <ul className="w-full min-w-0 space-y-0.5 text-sm">
        {data.map((d, i) => (
          <li key={`${baseId}-legend-${d.label}`}>
            <button
              type="button"
              onMouseEnter={() => setActive(i)}
              onMouseLeave={() => setActive(null)}
              onClick={() => select(i)}
              className={`flex w-full items-center gap-2 rounded px-1.5 py-1 text-left transition-colors ${
                active === i ? 'bg-slate-100' : 'hover:bg-slate-50'
              }`}
            >
              <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: colorFor(d, i) }} aria-hidden="true" />
              <span className="min-w-0 flex-1 truncate text-slate-600" title={d.label}>{d.label}</span>
              <span className="shrink-0 font-medium tabular-nums text-slate-700">{d.count}</span>
              <span className="w-9 shrink-0 text-right text-xs tabular-nums text-slate-400">{pct(d.count)}%</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
